/**
 * Coder feature helpers — pure display/derivation functions shared by the
 * wizard steps and the project-detail boards (design §10.2, §11.3, audit #6).
 */
import { useEffect, useState } from 'react';

import { formatDate } from '@/features/common/format';

import type { DocId, RepositoryView } from './types';

/** Raw Mongoose docs serialize `_id`; the coding-jobs mapped views carry `id`. */
export function docId(doc?: DocId | null): string {
  return doc?.id ?? doc?._id ?? '';
}

/** Provider display label; unknown provider → generic fallback (never invented). */
export function providerLabel(provider?: RepositoryView['provider']): string {
  if (provider === 'github') {
    return 'GitHub';
  }
  if (provider === 'gitea') {
    return 'Gitea';
  }
  return 'the provider';
}

/**
 * PR link-out derivation (design §10.2/A1, audit #3) — `PullRequest` carries no
 * URL field, so links build from the repository's `providerMeta`:
 * github → `https://github.com/{owner}/{repo}/pull/{n}`;
 * gitea → origin of `apiBase` + `/{owner}/{repo}/pulls/{n}`.
 * Underivable (missing provider/providerMeta/apiBase) → `undefined` — the row
 * renders unlinked; never a dead link.
 */
export function derivePullRequestUrl(
  repository: RepositoryView | undefined,
  providerNumber: number,
): string | undefined {
  const meta = repository?.providerMeta;
  if (!repository?.provider || !meta?.owner || !meta.repo) {
    return undefined;
  }
  if (repository.provider === 'github') {
    return `https://github.com/${meta.owner}/${meta.repo}/pull/${providerNumber}`;
  }
  if (repository.provider === 'gitea' && meta.apiBase) {
    try {
      const origin = new URL(meta.apiBase).origin;
      return `${origin}/${meta.owner}/${meta.repo}/pulls/${providerNumber}`;
    } catch {
      return undefined;
    }
  }
  return undefined;
}

/** Display host+path of a git URL (design §7.1); unparseable → the raw string. */
export function gitUrlDisplay(gitUrl?: string): string {
  if (!gitUrl) {
    return '';
  }
  try {
    const url = new URL(gitUrl);
    const path = url.pathname.replace(/\.git$/, '').replace(/\/$/, '');
    return `${url.host}${path}`;
  } catch {
    return gitUrl;
  }
}

/** 'javascript' → 'Javascript' (design §6.1: options first-letter capitalized
 * only — NOT canonical camelCase; 'JavaScript' would misstate the output). */
export function capitalizeFirst(value: string): string {
  if (!value) {
    return value;
  }
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/** Compact relative time for the jobs "Updated" column ("2m ago"); the full
 * timestamp stays available on the row's tooltip. */
export function formatRelativeTime(value?: string | number | null): string {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 0 || seconds < 45) {
    return 'just now';
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  return formatDate(value);
}

/** Full `formatDate` timestamp for tooltips next to relative-time cells. */
export function fullTimestamp(value?: string | number | null): string {
  return formatDate(value);
}

/**
 * `document.hidden` tracker for §11.4: the coding-jobs query polls only while
 * the document is visible and any job is non-terminal.
 */
export function useDocumentVisible(): boolean {
  const [visible, setVisible] = useState(
    () => typeof document === 'undefined' || document.visibilityState === 'visible',
  );

  useEffect(() => {
    const handleChange = () => setVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', handleChange);
    return () => document.removeEventListener('visibilitychange', handleChange);
  }, []);

  return visible;
}
